import React from "react";
import { X, AlertCircle } from "react-feather";
import "./status-alert-bar.scss";

/** Default color tokens matching Figma Pharmacy Prior Auth (node 1893-41347) */
export const STATUS_ALERT_BAR_DEFAULT_COLORS = {
  /** Container background (e.g. white). */
  backgroundColor: "#ffffff",
  /** Container border (e.g. light red #FFE8E8). */
  borderColor: "#ffe8e8",
  /** Primary accent: close circle, tag bg, main message, review text, alert icon. */
  accentColor: "#cc0300",
  /** Text/icon on accent (e.g. white for tag label and close X). */
  accentOnColor: "#ffffff",
} as const;

export interface StatusAlertBarColors {
  /** Container background. */
  backgroundColor?: string;
  /** Container border. */
  borderColor?: string;
  /** Primary accent (close circle, tag background, main message, review label, alert icon). */
  accentColor?: string;
  /** Color for content on accent areas (e.g. tag label text, close icon). */
  accentOnColor?: string;
}

export interface StatusAlertBarProps {
  /** Label inside the pill tag (e.g. "IF YES"). Omit or set showTag false to hide the tag. */
  tagLabel?: string;
  /** Main message (e.g. "Stop Process"). */
  mainMessage: string;
  /** Right-side label (e.g. "Human Review"). Omit or set showReviewLabel false to hide. */
  reviewLabel?: string;
  /** When true, show the left icon slot; if leftIcon is not passed, default X is used. */
  showLeftIcon?: boolean;
  /**
   * Left icon. Pass any React node (e.g. <X />, <Trash2 />, <Info />, or custom SVG).
   * When passed, this icon is shown and can be anything (not only a close icon). When not passed and showLeftIcon is true, default X is used.
   */
  leftIcon?: React.ReactNode;
  /** Called when the left icon is clicked. */
  onLeftIconClick?: () => void;
  /** @deprecated Use showLeftIcon instead. */
  showCloseIcon?: boolean;
  /** @deprecated Use leftIcon instead. */
  closeIcon?: React.ReactNode;
  /** @deprecated Use onLeftIconClick instead. */
  onClose?: () => void;
  /** Show the pill tag. */
  showTag?: boolean;
  /** Show the right alert icon next to review label. */
  showAlertIcon?: boolean;
  /** Custom colors; each overrides the default. Use for theming across the app. */
  colors?: StatusAlertBarColors;
  /** Optional class name for the root. */
  className?: string;
  /** Icon size in pixels (applied to default icons and to the icon containers). Default 16. */
  iconSize?: number;
  /**
   * Left icon size in pixels. When not set, defaults to ~87.5% of iconSize so the left icon is slightly smaller.
   * Set to match iconSize or any value for full control.
   */
  leftIconSize?: number;
  /**
   * Alert/review icon shown next to reviewLabel. Pass any React node (e.g. <AlertCircle />, <Info size={16} />, or custom SVG).
   * When not provided, the default AlertCircle icon is used. Use with showAlertIcon.
   */
  alertIcon?: React.ReactNode;
}

const defaultColors = STATUS_ALERT_BAR_DEFAULT_COLORS;

export function StatusAlertBar({
  tagLabel = "IF YES",
  mainMessage,
  reviewLabel = "Human Review",
  showLeftIcon,
  showCloseIcon = true,
  leftIcon,
  closeIcon,
  onLeftIconClick,
  onClose,
  showTag = true,
  showAlertIcon = true,
  colors = {},
  className = "",
  iconSize = 16,
  leftIconSize,
  alertIcon,
}: StatusAlertBarProps) {
  const effectiveLeftIconSize = leftIconSize ?? Math.max(10, Math.round(iconSize * 0.875));
  const effectiveLeftIcon = leftIcon ?? closeIcon;
  const showLeftSlot = showLeftIcon ?? showCloseIcon;
  const hasLeftSlot = effectiveLeftIcon != null || showLeftSlot;
  const renderLeftIcon = effectiveLeftIcon ?? <X size={effectiveLeftIconSize} strokeWidth={2} />;
  const handleLeftClick = onLeftIconClick ?? onClose;

  const bg = colors.backgroundColor ?? defaultColors.backgroundColor;
  const border = colors.borderColor ?? defaultColors.borderColor;
  const accent = colors.accentColor ?? defaultColors.accentColor;
  const accentOn = colors.accentOnColor ?? defaultColors.accentOnColor;

  const rootStyle = {
    ["--status-alert-bar-bg" as string]: bg,
    ["--status-alert-bar-border" as string]: border,
    ["--status-alert-bar-accent" as string]: accent,
    ["--status-alert-bar-accent-on" as string]: accentOn,
    ["--status-alert-bar-icon-size" as string]: `${iconSize / 16}rem`,
    ["--status-alert-bar-left-icon-inner-size" as string]: `${effectiveLeftIconSize / 16}rem`,
  } as React.CSSProperties;

  return (
    <div
      className={`status-alert-bar ${className}`.trim()}
      style={rootStyle}
      data-name="StatusAlertBar"
    >
      <div className="status-alert-bar__row">
        {hasLeftSlot && (
          <button
            type="button"
            className="status-alert-bar__left-icon"
            onClick={(e) => {
              e.stopPropagation();
              handleLeftClick?.();
            }}
            aria-label={effectiveLeftIcon != null ? "Left action" : "Close"}
          >
            <span className="status-alert-bar__left-icon-inner">{renderLeftIcon}</span>
          </button>
        )}

        <div className="status-alert-bar__content">
          {showTag && tagLabel != null && tagLabel !== "" && (
            <span className="status-alert-bar__tag">{tagLabel}</span>
          )}
          <span className="status-alert-bar__message">{mainMessage}</span>
        </div>

        {(showAlertIcon || (reviewLabel != null && reviewLabel !== "")) && (
          <div className="status-alert-bar__review">
            {showAlertIcon && (
              <span className="status-alert-bar__review-icon" aria-hidden>
                {alertIcon ?? <AlertCircle size={iconSize} strokeWidth={2} />}
              </span>
            )}
            {reviewLabel != null && reviewLabel !== "" && (
              <span className="status-alert-bar__review-label">{reviewLabel}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
